import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import { CASHBOX_ROLE_LABELS } from 'utilities/helper/cashBoxes'
import {
    activateCashbox,
    deleteCashboxShareGroup,
    inviteToCashbox,
    kickOutMember,
    updateMembershipShares,
} from 'services/cashbox'
import {
    Card,
    Field,
    Notice,
    PrimaryButton,
    SecondaryButton,
    TextInput,
    apiMessage,
    inputClass,
    isValidPhone,
    normalizePhone,
    parseDigits,
} from 'modules/cashbox/ui'
import {
    CASHBOX_KEY,
    useCashbox,
    useCashboxMembers,
    useShareGroupView,
} from 'modules/cashbox/hooks'

/**
 * مدیریت اعضای صندوق — the manager's members screen.
 *
 * A cashbox has no join link: every member gets here because the manager typed
 * their phone number. `membership/invite/` creates the Member row if the phone
 * has never used Hamyan, and the membership is accepted on both sides
 * immediately (`is_owner_accept`/`is_member_accept` both true), so there is no
 * pending state to chase — which is exactly how Android behaves.
 *
 * The backend's editing rules, reproduced here so the buttons tell the truth
 * instead of surfacing a 400 after the fact:
 *   - invite: blocked once the box is active past cycle 1, unless
 *     `editable_members` is on;
 *   - change shares: only while `cycle_index <= 1`, and never below what the
 *     member has already paid;
 *   - remove: never the owner, never someone with a successful transaction,
 *     and not once the box is active past cycle 1.
 */
export default function CashboxMembersPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { cashbox, isLoading, isError, refetch } = useCashbox(id)
    const {
        data,
        isLoading: membersLoading,
        refetch: refetchMembers,
    } = useCashboxMembers(id)

    const [showAdd, setShowAdd] = useState(false)
    const [phone, setPhone] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [shares, setShares] = useState(1)
    const [editing, setEditing] = useState<ICashboxMembership | null>(null)
    const [editShares, setEditShares] = useState(0)

    const { wholeShareMembers, groupViews } = useShareGroupView(
        data?.memberships,
        data?.share_groups,
        cashbox?.period?.share_value ?? 0
    )

    const invalidate = () => {
        queryClient.invalidateQueries(CASHBOX_KEY(id))
        queryClient.invalidateQueries(['cashbox-members', id])
        queryClient.invalidateQueries(['get-boxes'])
    }

    const invite = useMutation({
        mutationFn: () =>
            inviteToCashbox({
                id: id!,
                phone_number: normalizePhone(phone),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                number_of_shares: shares,
            }),
        onSuccess: () => {
            // Nothing about WHO was invited — the phone number and the name are
            // the whole payload of this call and none of it goes to PostHog.
            posthog.capture('cashbox_member_invited', {
                cashbox_id: id,
                number_of_shares: shares,
            })
            toast.success('عضو اضافه شد')
            setPhone('')
            setFirstName('')
            setLastName('')
            setShares(1)
            setShowAdd(false)
            invalidate()
        },
        onError: (error: any) => {
            posthog.capture('cashbox_member_invite_failed', {
                cashbox_id: id,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    const changeShares = useMutation({
        mutationFn: (payload: { membershipId: number; value: number }) =>
            updateMembershipShares({
                id: id!,
                membershipId: payload.membershipId,
                number_of_shares: payload.value,
            }),
        onSuccess: () => {
            toast.success('تعداد سهم به‌روزرسانی شد')
            setEditing(null)
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const remove = useMutation({
        mutationFn: (membershipId: number) =>
            kickOutMember({ id: id!, membershipId }),
        onSuccess: () => {
            toast.success('عضو حذف شد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const removeGroup = useMutation({
        mutationFn: (shareGroupId: number) =>
            deleteCashboxShareGroup({ id: id!, shareGroupId }),
        onSuccess: () => {
            toast.success('گروه حذف شد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const activate = useMutation({
        mutationFn: () => activateCashbox(id!),
        onSuccess: () => {
            // The conversion this whole product hangs on: a box that is never
            // activated never runs a cycle, never takes a payment and never
            // draws. Everything upstream of here is setup.
            posthog.capture('cashbox_activated', {
                cashbox_id: id,
                total_shares: totalShares,
                number_of_loans: numberOfLoans,
                groups_count: groupViews.length,
            })
            toast.success('صندوق فعال شد')
            invalidate()
            navigate(`/cashbox/${id}`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_activate_failed', {
                cashbox_id: id,
                total_shares: totalShares,
                number_of_loans: numberOfLoans,
                groups_count: groupViews.length,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    const period = cashbox?.period
    const totalShares = period?.total_shares ?? 0
    const numberOfLoans = period?.number_of_loans ?? 1

    /**
     * `activate_cashbox`'s own preconditions, checked before we let the button
     * fire so the manager sees what to fix rather than an English 400/401.
     *
     * Each one carries a stable `code` next to its Persian sentence: the
     * sentence names the group and quotes amounts, the code is what analytics
     * gets.
     */
    const activationBlockers = useMemo(() => {
        const blockers: Array<{ code: string; message: string }> = []
        if (!period) return blockers
        if (totalShares === 0)
            blockers.push({
                code: 'no_shares',
                message: 'هنوز هیچ سهمی در صندوق نیست',
            })
        if (numberOfLoans === 0 || numberOfLoans > totalShares)
            blockers.push({
                code: 'loans_exceed_shares',
                message: `تعداد وام (${faPlain.format(
                    numberOfLoans
                )}) نمی‌تواند از تعداد کل سهم‌ها (${faPlain.format(
                    totalShares
                )}) بیشتر باشد`,
            })
        else if (totalShares % numberOfLoans !== 0)
            blockers.push({
                code: 'shares_not_divisible',
                message: `تعداد کل سهم‌ها (${faPlain.format(
                    totalShares
                )}) باید بر تعداد وام (${faPlain.format(
                    numberOfLoans
                )}) بخش‌پذیر باشد`,
            })
        if (period.day_of_draw === 0)
            blockers.push({
                code: 'no_draw_day',
                message: 'روز قرعه‌کشی تعیین نشده',
            })
        groupViews
            .filter((view) => !view.isBalanced)
            .forEach((view) =>
                blockers.push({
                    code: 'unbalanced_share_group',
                    message: `گروه «${
                        view.group.name || 'بدون نام'
                    }» ناتراز است (${toman(view.total)} به‌جای ${toman(
                        period.share_value
                    )})`,
                })
            )
        return blockers
    }, [period, totalShares, numberOfLoans, groupViews])

    const state = cashbox?.state
    const membersCount = data?.memberships?.length
    useEffect(() => {
        if (!cashbox || !data) return
        posthog.capture('cashbox_members_viewed', {
            cashbox_id: id,
            state,
            total_shares: totalShares,
            members_count: membersCount,
            groups_count: groupViews.length,
        })
    }, [cashbox, data, id, state, totalShares, membersCount, groupViews.length])

    // Why an activation never happened. `unbalanced_share_group` in particular
    // is invisible from the box itself — the shares are all there, they just do
    // not add up — so without this the box looks abandoned rather than stuck.
    // De-duplicated because several unbalanced groups are one reason, not many.
    const blockerKey = Array.from(
        new Set(activationBlockers.map((blocker) => blocker.code))
    ).join(',')
    const activationVisible = state !== 'act' && state !== 'unp'
    useEffect(() => {
        // `data` too: the group blockers need the members list, and firing
        // before it lands would report the box as blocked for the wrong reason.
        if (!data || !activationVisible || !blockerKey) return
        posthog.capture('cashbox_activation_blocked', {
            cashbox_id: id,
            blocker_codes: blockerKey.split(','),
            total_shares: totalShares,
            number_of_loans: numberOfLoans,
        })
    }, [data, activationVisible, blockerKey, id, totalShares, numberOfLoans])

    if (isLoading || membersLoading) return <DotsLoading />
    if (isError || !cashbox)
        return (
            <div className="bg-white min-h-screen">
                <Header title="اعضای صندوق" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="اعضای صندوق" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند اعضا را مدیریت کند.
                    </Notice>
                </div>
            </div>
        )

    const isActive = cashbox.state === 'act'
    // `unp` = «در انتظار پرداخت اشتراک». `activate_cashbox` does not itself
    // refuse an unpaid box, so without this gate a manager could activate one
    // and leave it running on a plan that was never settled.
    const isUnpaid = cashbox.state === 'unp'
    const pastFirstCycle = (period?.cycle_index ?? 0) > 1
    const canInvite = !isActive || !pastFirstCycle || cashbox.editable_members
    const canEditShares = (period?.cycle_index ?? 0) <= 1
    const canRemove = !isActive || !pastFirstCycle

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="اعضای صندوق" />
            </div>

            <div className="px-4 pt-3">
                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            تعداد کل سهم‌ها
                        </span>
                        <span className="text-sm font-bold text-black">
                            {faPlain.format(totalShares)} سهم
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            جمع هر نوبت
                        </span>
                        <span className="text-sm text-black">
                            {toman(period?.satisfactory_amount ?? 0)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            مبلغ هر وام
                        </span>
                        <span className="text-sm text-black">
                            {toman(period?.loan_amount ?? 0)}
                        </span>
                    </div>
                </Card>

                {!canInvite && (
                    <div className="mb-3">
                        <Notice tone="warn">
                            صندوق فعال است و از نوبت اول گذشته؛ برای افزودن یا
                            حذف عضو باید گزینه‌ی «امکان تغییر اعضا» را در
                            تنظیمات روشن کنید.
                        </Notice>
                    </div>
                )}

                {/* add member ------------------------------------------- */}
                {canInvite &&
                    (showAdd ? (
                        <Card className="mb-3">
                            <p className="text-sm font-bold text-black mb-3">
                                افزودن عضو
                            </p>
                            <Field
                                label="شماره موبایل"
                                error={
                                    phone && !isValidPhone(phone)
                                        ? 'شماره موبایل معتبر نیست'
                                        : undefined
                                }
                                hint="مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
                            >
                                <TextInput
                                    inputMode="tel"
                                    value={phone}
                                    onChange={(event) =>
                                        setPhone(event.target.value)
                                    }
                                    invalid={Boolean(
                                        phone && !isValidPhone(phone)
                                    )}
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-2">
                                <Field label="نام">
                                    <TextInput
                                        value={firstName}
                                        onChange={(event) =>
                                            setFirstName(event.target.value)
                                        }
                                    />
                                </Field>
                                <Field label="نام خانوادگی">
                                    <TextInput
                                        value={lastName}
                                        onChange={(event) =>
                                            setLastName(event.target.value)
                                        }
                                    />
                                </Field>
                            </div>
                            <Field label="تعداد سهم">
                                <TextInput
                                    inputMode="numeric"
                                    value={String(shares)}
                                    onChange={(event) =>
                                        setShares(
                                            parseDigits(event.target.value)
                                        )
                                    }
                                />
                            </Field>
                            <div className="flex gap-2">
                                <PrimaryButton
                                    disabled={
                                        !isValidPhone(phone) ||
                                        shares < 1 ||
                                        invite.isLoading
                                    }
                                    onClick={() => invite.mutate()}
                                >
                                    {invite.isLoading
                                        ? 'در حال افزودن…'
                                        : 'افزودن'}
                                </PrimaryButton>
                                <SecondaryButton
                                    onClick={() => setShowAdd(false)}
                                >
                                    انصراف
                                </SecondaryButton>
                            </div>
                        </Card>
                    ) : (
                        <div className="flex gap-2 mb-3">
                            <PrimaryButton onClick={() => setShowAdd(true)}>
                                + افزودن عضو
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() =>
                                    navigate(`/cashbox/${id}/share-group`)
                                }
                            >
                                + گروه خرده‌سهم
                            </SecondaryButton>
                        </div>
                    ))}

                {/* share groups ----------------------------------------- */}
                {groupViews.length > 0 && (
                    <>
                        <p className="text-sm font-bold text-black mb-2 mt-4">
                            گروه‌های خرده‌سهم
                        </p>
                        {groupViews.map((view) => (
                            <Card key={view.group.id} className="mb-3">
                                <div className="flex items-start justify-between">
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-black truncate">
                                            {view.group.name || 'گروه بدون نام'}
                                        </p>
                                        <p className="text-xs text-text-secondary mt-0.5">
                                            {faPlain.format(
                                                view.members.length
                                            )}{' '}
                                            نفر · مجموع {toman(view.total)}
                                        </p>
                                    </div>
                                    {view.group.is_won && (
                                        <span className="text-xs font-bold text-[#4CA632] shrink-0">
                                            برنده شده
                                        </span>
                                    )}
                                </div>

                                {!view.isBalanced && (
                                    <p className="text-xs text-[#B3261E] mt-2 leading-5">
                                        مجموع سهم اعضای این گروه با ارزش هر سهم
                                        ({toman(period?.share_value ?? 0)})
                                        برابر نیست. تا اصلاح، صندوق فعال
                                        نمی‌شود.
                                    </p>
                                )}

                                <div className="mt-2">
                                    {view.members.map((member) => (
                                        <div
                                            key={member.id}
                                            className="flex justify-between text-xs text-text-body py-1"
                                        >
                                            <span className="truncate">
                                                {member.name?.trim() ||
                                                    member.phone_number}
                                            </span>
                                            <span>
                                                {toman(member.share_residue)}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {canRemove && !view.group.is_won && (
                                    <div className="flex gap-2 mt-3">
                                        <SecondaryButton
                                            className="h-10"
                                            onClick={() =>
                                                navigate(
                                                    `/cashbox/${id}/share-group/${view.group.id}`
                                                )
                                            }
                                        >
                                            ویرایش
                                        </SecondaryButton>
                                        <SecondaryButton
                                            className="h-10 text-[#B3261E] border-[#F5C2BD]"
                                            disabled={removeGroup.isLoading}
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        'این گروه خرده‌سهم حذف شود؟'
                                                    )
                                                )
                                                    removeGroup.mutate(
                                                        view.group.id
                                                    )
                                            }}
                                        >
                                            حذف گروه
                                        </SecondaryButton>
                                    </div>
                                )}
                            </Card>
                        ))}
                    </>
                )}

                {/* members ---------------------------------------------- */}
                <p className="text-sm font-bold text-black mb-2 mt-4">
                    اعضا ({faPlain.format(wholeShareMembers.length)})
                </p>
                {wholeShareMembers.map((member) => (
                    <Card key={member.id} className="mb-2">
                        <div className="flex items-start justify-between">
                            <div className="min-w-0">
                                <p className="text-sm text-black truncate">
                                    {member.name?.trim() ||
                                        member.phone_number ||
                                        'عضو بدون نام'}
                                </p>
                                <p className="text-xs text-text-secondary mt-0.5">
                                    {member.role !== 'nrm' &&
                                        `${
                                            CASHBOX_ROLE_LABELS[member.role]
                                        } · `}
                                    {faPlain.format(member.number_of_shares)} سهم
                                    {member.phone_number
                                        ? ` · ${member.phone_number}`
                                        : ''}
                                </p>
                            </div>
                            <div className="text-left shrink-0 pl-1">
                                <p className="text-xs text-text-secondary">
                                    باقی‌مانده
                                </p>
                                <p className="text-sm font-bold text-black">
                                    {member.remaining_share_amount > 0
                                        ? toman(member.remaining_share_amount)
                                        : 'تسویه'}
                                </p>
                            </div>
                        </div>

                        {editing?.id === member.id ? (
                            <div className="mt-3">
                                <Field
                                    label="تعداد سهم"
                                    hint={
                                        isActive
                                            ? `کمتر از پرداختی فعلی (${toman(
                                                  member.balance
                                              )}) پذیرفته نمی‌شود`
                                            : undefined
                                    }
                                >
                                    <TextInput
                                        inputMode="numeric"
                                        value={String(editShares)}
                                        onChange={(event) =>
                                            setEditShares(
                                                parseDigits(event.target.value)
                                            )
                                        }
                                    />
                                </Field>
                                <div className="flex gap-2">
                                    <PrimaryButton
                                        className="h-10"
                                        disabled={changeShares.isLoading}
                                        onClick={() =>
                                            changeShares.mutate({
                                                membershipId: member.id,
                                                value: editShares,
                                            })
                                        }
                                    >
                                        ذخیره
                                    </PrimaryButton>
                                    <SecondaryButton
                                        className="h-10"
                                        onClick={() => setEditing(null)}
                                    >
                                        انصراف
                                    </SecondaryButton>
                                </div>
                            </div>
                        ) : (
                            (canEditShares || canRemove) && (
                                <div className="flex gap-2 mt-3">
                                    {canEditShares && (
                                        <SecondaryButton
                                            className="h-9 text-xs"
                                            onClick={() => {
                                                setEditing(member)
                                                setEditShares(
                                                    member.number_of_shares
                                                )
                                            }}
                                        >
                                            تغییر سهم
                                        </SecondaryButton>
                                    )}
                                    {canRemove && member.role !== 'own' && (
                                        <SecondaryButton
                                            className="h-9 text-xs text-[#B3261E] border-[#F5C2BD]"
                                            disabled={remove.isLoading}
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        'این عضو از صندوق حذف شود؟'
                                                    )
                                                )
                                                    remove.mutate(member.id)
                                            }}
                                        >
                                            حذف
                                        </SecondaryButton>
                                    )}
                                </div>
                            )
                        )}
                    </Card>
                ))}

                {/* activation ------------------------------------------- */}
                {!isActive && isUnpaid && (
                    <div className="mt-5">
                        <Notice tone="warn">
                            نسخه‌ی این صندوق هنوز مشخص نشده است. تا وقتی نسخه‌ی
                            کارمزدی انتخاب یا اشتراک پرداخت نشود، صندوق فعال
                            نمی‌شود.
                        </Notice>
                        <div className="mt-3">
                            <PrimaryButton
                                onClick={() => navigate(`/cashbox/${id}/plans`)}
                            >
                                انتخاب نسخه‌ی صندوق
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {!isActive && !isUnpaid && (
                    <div className="mt-5">
                        {activationBlockers.length > 0 ? (
                            <Notice tone="warn">
                                برای فعال کردن صندوق:
                                <br />
                                {activationBlockers.map((blocker, index) => (
                                    <span key={`${blocker.code}-${index}`}>
                                        • {blocker.message}
                                        <br />
                                    </span>
                                ))}
                            </Notice>
                        ) : (
                            <Notice tone="info">
                                با فعال‌سازی، نوبت اول شروع می‌شود و تا پایان
                                نوبت اول امکان تغییر اعضا محدود می‌شود.
                            </Notice>
                        )}
                        <div className="mt-3">
                            <PrimaryButton
                                disabled={
                                    activationBlockers.length > 0 ||
                                    activate.isLoading
                                }
                                onClick={() => activate.mutate()}
                            >
                                {activate.isLoading
                                    ? 'در حال فعال‌سازی…'
                                    : 'فعال‌سازی صندوق'}
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                <div className="mt-3">
                    <SecondaryButton onClick={() => refetchMembers()}>
                        به‌روزرسانی فهرست
                    </SecondaryButton>
                </div>
            </div>
        </div>
    )
}
